import { useCallback, useEffect, useState } from 'react';
import {
  fetchPaymentJob,
  fetchPaymentReport,
  runPayment,
  startPaymentJob,
  type PaymentReportInfo,
} from '@/api/payment-prep';
import { toMessage } from '@/api/to-message';
import { BankAccountSection } from '@/components/BankAccountSection';
import { PaymentJobPanel, type PaymentJobView } from '@/components/PaymentJobPanel';
import { PaymentProgressDialog, type PaymentDialogState } from '@/components/PaymentProgressDialog';
import { PaymentReportLists } from '@/components/PaymentReportLists';
import { PaymentStepList } from '@/components/PaymentStepList';
import {
  PAYMENT_CYCLES,
  remindTargetsOf,
  summarizeJobResult,
  type PaymentCycle,
  type PaymentStep,
} from '@/payment/payment-steps';
import { OUTLINE_PILL, PRIMARY_PILL, SMALL_PILL } from '@/payment/payment-styles';
import { waitForJob } from '@/payment/wait-for-job';

/** 一覧の読み込み状態 */
type ReportState =
  | { phase: 'loading' }
  | { phase: 'loaded'; info: PaymentReportInfo }
  | { phase: 'failed'; message: string };

/**
 * @graph-node {Page}
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 支払準備の画面。15日 / 25日の支払を選び、支払管理レポートと証憑整理フォルダの一覧を見ながら、8 ステップを上から順に進める。レポート作成・証憑整理は GAS のジョブとして動かし、進みは右下に退けてほかの操作を続けられる。リマインド送信・Old への移動は確認してから動かす
 * @graph-connects fetchPaymentReport [calls] レポートとフォルダの一覧を読む
 * @graph-connects startPaymentJob [calls] レポート作成・証憑整理を始める
 * @graph-connects waitForJob [calls] ジョブが終わるのを待つ
 * @graph-connects runPayment [calls] リマインドの対象確認・送信と Old への移動を動かす
 * @graph-connects summarizeJobResult [calls] 終わったときの文言とリンクを組み立てる
 * @graph-connects BankAccountSection [calls] 振込先口座が未登録の取引先と登録
 */
export function PaymentPrepPanel() {
  const [cycle, setCycle] = useState<PaymentCycle>('15日');
  const [report, setReport] = useState<ReportState>({ phase: 'loading' });
  // 確認・送信・移動のポップアップ（画面を止める）
  const [dialog, setDialog] = useState<PaymentDialogState | null>(null);
  // GAS のジョブ（画面を止めない。右下に退けられる）
  const [job, setJob] = useState<PaymentJobView | null>(null);
  const [jobExpanded, setJobExpanded] = useState(true);

  const loadReport = useCallback(async () => {
    setReport({ phase: 'loading' });
    try {
      setReport({ phase: 'loaded', info: await fetchPaymentReport() });
    } catch (cause: unknown) {
      setReport({ phase: 'failed', message: toMessage(cause) });
    }
  }, []);

  useEffect(() => {
    void loadReport();
  }, [loadReport]);

  const fail = (title: string, cause: unknown) => {
    setDialog({ phase: 'error', title, message: toMessage(cause) });
  };

  // GAS のジョブは同時に 1 つしか動かせない
  const jobRunning = job?.phase === 'running';

  // レポート作成・証憑整理。始めは真ん中に出し、外側を押すと右下に退く
  const runJob = async (fn: string, title: string) => {
    const base: PaymentJobView = {
      title,
      phase: 'running',
      step: '開始しています…',
      current: null,
      total: null,
      message: '',
      links: [],
    };
    setJob(base);
    setJobExpanded(true);
    try {
      await startPaymentJob(fn);
      const done = await waitForJob({
        fetchJob: fetchPaymentJob,
        onProgress: (progress) => {
          setJob({
            ...base,
            step: progress.step === '' ? '実行中…' : progress.step,
            current: progress.current,
            total: progress.total,
          });
        },
      });
      setJob({ ...base, phase: 'done', ...summarizeJobResult(title, done.result) });
      void loadReport();
    } catch (cause: unknown) {
      setJob({ ...base, phase: 'error', message: toMessage(cause) });
    }
  };

  // リマインド。送る相手を先に見せ、確かめてから送る
  const runRemind = async () => {
    const title = `リマインド送信（${cycle}分）`;
    setDialog({
      phase: 'running',
      title,
      step: '対象を確認しています…',
      current: null,
      total: null,
    });
    try {
      const targets = remindTargetsOf(await runPayment(`リマインドプレビュー${cycle}`));
      if (targets.length === 0) {
        setDialog({
          phase: 'done',
          title,
          message: '送る相手がいません（証憑未着の取引先はありません）',
          links: [],
        });
        return;
      }
      setDialog({
        phase: 'confirm',
        title,
        message: `次の ${String(targets.length)} 社へ、Slack で証憑の送付を依頼します。`,
        items: targets,
        confirmLabel: '送信する',
        onConfirm: () => {
          void sendRemind(title, targets.length);
        },
      });
    } catch (cause: unknown) {
      fail(title, cause);
    }
  };

  const sendRemind = async (title: string, count: number) => {
    setDialog({ phase: 'running', title, step: '送信しています…', current: null, total: null });
    try {
      await runPayment(`リマインド送信${cycle}`);
      setDialog({
        phase: 'done',
        title,
        message: `Slack へ送信しました（${cycle}分・${String(count)} 社）`,
        links: [],
      });
    } catch (cause: unknown) {
      fail(title, cause);
    }
  };

  // レポート・証憑整理フォルダを Old（アーカイブ）へ移す。確かめてから動かす
  const moveToOld = (name: string, id: string) => {
    const title = 'Old フォルダへ移動';
    setDialog({
      phase: 'confirm',
      title,
      message: `「${name}」を Old フォルダへ移動します。`,
      items: [],
      confirmLabel: '移動する',
      onConfirm: () => {
        setDialog({ phase: 'running', title, step: '移動しています…', current: null, total: null });
        runPayment('証憑フォルダをOldへ移動', id)
          .then(() => {
            setDialog({
              phase: 'done',
              title,
              message: `「${name}」を Old フォルダへ移動しました`,
              links: [],
            });
            void loadReport();
          })
          .catch((cause: unknown) => {
            fail(title, cause);
          });
      },
    });
  };

  const latestUrl = report.phase === 'loaded' ? (report.info.reports[0]?.url ?? '') : '';

  const renderAction = (step: PaymentStep) => {
    const { action } = step;
    switch (action.type) {
      case 'job':
        return (
          <button
            type="button"
            disabled={jobRunning}
            title={jobRunning ? '別の処理が動いています。終わってから押してください' : undefined}
            onClick={() => {
              void runJob(action.fn(cycle), step.title);
            }}
            className={PRIMARY_PILL}
          >
            {action.label(cycle)}
          </button>
        );
      case 'remind':
        return (
          <button
            type="button"
            onClick={() => {
              void runRemind();
            }}
            className={PRIMARY_PILL}
          >
            {action.label(cycle)}
          </button>
        );
      case 'sheet':
        return latestUrl === '' ? (
          <span className="shrink-0 text-xs text-[var(--color-text-muted)]">
            レポートがまだありません
          </span>
        ) : (
          <a href={latestUrl} target="_blank" rel="noreferrer" className={OUTLINE_PILL}>
            {action.label}
          </a>
        );
      case 'link':
        return (
          <a href={action.href} target="_blank" rel="noreferrer" className={OUTLINE_PILL}>
            {action.label}
          </a>
        );
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-center gap-3">
        <div
          role="radiogroup"
          aria-label="支払のサイクル"
          className="flex gap-1 rounded-full bg-[var(--color-bg-subtle)] p-1"
        >
          {PAYMENT_CYCLES.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={cycle === value}
              onClick={() => {
                setCycle(value);
              }}
              className={
                cycle === value
                  ? 'rounded-full bg-[var(--color-bg-surface)] px-4 py-1 text-sm font-semibold text-[var(--color-doing-text)] shadow-[0_1px_3px_var(--color-shadow)]'
                  : 'rounded-full px-4 py-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)]'
              }
            >
              {value}支払
            </button>
          ))}
        </div>
        <p className="m-0 text-xs text-[var(--color-text-muted)]">
          上から順に進めます。レポートの確認・編集はスプレッドシートで行います
        </p>
        <button
          type="button"
          onClick={() => {
            void loadReport();
          }}
          className={`${SMALL_PILL} ml-auto`}
        >
          一覧を読み込み直す
        </button>
      </div>

      {report.phase === 'loading' ? (
        <p className="m-0 text-sm text-[var(--color-text-muted)]">一覧を読み込んでいます…</p>
      ) : null}
      {report.phase === 'failed' ? (
        <p
          role="alert"
          className="m-0 rounded-2xl bg-[var(--color-kind-closing-bg)] px-4 py-3 text-sm text-[var(--color-overdue)]"
        >
          一覧を読み込めませんでした：{report.message}
        </p>
      ) : null}
      {report.phase === 'loaded' ? (
        <PaymentReportLists info={report.info} onMoveToOld={moveToOld} />
      ) : null}

      <BankAccountSection />

      <PaymentStepList renderAction={renderAction} />

      {job === null ? null : (
        <PaymentJobPanel
          job={job}
          // 確認のポップアップを出している間は、ジョブは右下に退けておく
          expanded={jobExpanded && dialog === null}
          onExpand={() => {
            setJobExpanded(true);
          }}
          onMinimize={() => {
            setJobExpanded(false);
          }}
          onClose={() => {
            setJob(null);
          }}
        />
      )}

      {dialog === null ? null : (
        <PaymentProgressDialog
          state={dialog}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </div>
  );
}
